import { useEffect } from 'react'
import { UseMutateFunction, useQueryClient } from '@tanstack/react-query'
import { changeUserLanguage as changeUserLanguageQuery, getUserLanguage } from '../api/user.api'
import { useTypedSelector } from 'src/State/RootReducer'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AppLang } from '@e3dc-react/shell/State/App/AppModel'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { usePortalMutation, usePortalQuery } from 'src/Libs/api'
import { changeAppLanguage } from 'src/Libs/languages/functions/default'

interface Variables {
  language: AppLang
}

interface UseUserLanguageReturnType {
  userLanguage: AppLang | null | undefined
  isUserLanguageLoading: boolean
  changeUserLanguage: UseMutateFunction<void | null, unknown, Variables, unknown>
  invalidateUserLanguage: () => void
}

const useUserLanguage = (): UseUserLanguageReturnType => {
  const userId = useTypedSelector(state => state.auth.userId)
  const authFetch = useAuthFetchWithResponse()
  const { t } = useCustomTranslation()
  const { enqueueErrorStack } = useSnackbar()
  const queryClient = useQueryClient()

  const getUserLanguageQueryFn = async (): Promise<AppLang | null> => {
    const { error, hasErrors, value } = await getUserLanguage(userId, authFetch)

    if (hasErrors) {
      throw error || new Error('An unexpected error occurred when fetching user language')
    }

    return value
  }

  const { data: userLanguage, isLoading: isUserLanguageLoading } = usePortalQuery({
    queryKey: ['users', userId, 'settings', 'language'],
    queryFn: getUserLanguageQueryFn,
    enabled: !!userId,
  })

  const { mutate: changeUserLanguage } = usePortalMutation({
    mutationFn: (variables: Variables) => changeUserLanguageQuery(userId!, variables.language, authFetch),
    onError: () => enqueueErrorStack(t('errors.save-language-error')),
  })

  useEffect(() => {
    if (userLanguage && !isUserLanguageLoading) {
      changeAppLanguage(userLanguage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLanguage, isUserLanguageLoading])

  const invalidateUserLanguage = (): void => {
    queryClient.invalidateQueries({ queryKey: ['users', userId, 'settings', 'language'] })
  }

  return { userLanguage, changeUserLanguage, isUserLanguageLoading, invalidateUserLanguage }
}

export default useUserLanguage
